import React, { useState, useEffect } from 'react';
import { interactiveLearningService } from '../../services/interactiveLearningService';
import { progressService } from '../../services/firestoreService';
import { activityLogService } from '../../services/activityLogService';
import './LearningCards.css';

const LearningCards = ({ courseId, topicName, userId, userName }) => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completedCards, setCompletedCards] = useState(new Set());
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    const loadCards = async () => {
      try {
        const content = await interactiveLearningService.getTopicContent(courseId, topicName);
        if (content?.learningCards && content.learningCards.length > 0) {
          setCards(content.learningCards);
        }
        
        // Load completed cards for this user
        const progress = await progressService.getLearningCardProgress(userId, courseId, topicName);
        if (progress && progress.completedCards) {
          setCompletedCards(new Set(progress.completedCards));
        }
      } catch (error) {
        console.debug('No learning cards found for topic:', topicName);
      } finally {
        setLoading(false);
      }
    };

    if (courseId && topicName && userId) {
      loadCards();
    }
  }, [courseId, topicName, userId]);

  const handleCardComplete = async (cardId) => {
    try {
      await progressService.markLearningCardComplete(userId, courseId, topicName, cardId);
      // Log learning card completion
      await activityLogService.logActivity(userId, activityLogService.ACTIVITY_TYPES.COMPLETE_LEARNING_CARD, {
        courseId,
        topicName,
        cardId
      });
      setCompletedCards(prev => new Set([...prev, cardId]));
    } catch (error) {
      console.error('Error marking card as complete:', error);
      alert('Error saving progress. Please try again.');
    }
  };

  const handleCardExpand = (cardId) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  if (loading) {
    return (
      <div className="learning-cards-container">
        <p>Loading learning cards...</p>
      </div>
    );
  }

  if (!cards || cards.length === 0) {
    return null;
  }

  return (
    <div className="learning-cards-container">
      <h4>📚 Learning Cards</h4>
      <p className="cards-description">Learn about key concepts step by step!</p>
      <div className="cards-grid">
        {cards.map((card, index) => {
          const isCompleted = completedCards.has(card.id || index);
          const isExpanded = expandedCard === (card.id || index);
          
          return (
            <div
              key={card.id || index}
              className={`learning-card ${isCompleted ? 'completed' : ''} ${isExpanded ? 'expanded' : ''}`}
            >
              <div className="card-header" onClick={() => handleCardExpand(card.id || index)}>
                <div className="card-number">#{index + 1}</div>
                <div className="card-title-section">
                  <h5>{card.title}</h5>
                  {card.subtitle && <p className="card-subtitle">{card.subtitle}</p>}
                </div>
                <div className="card-actions">
                  {isCompleted && (
                    <span className="completed-badge">✅</span>
                  )}
                  <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                </div>
              </div>
              
              {isExpanded && (
                <div className="card-content">
                  {card.description && (
                    <div className="card-description">
                      <p>{card.description}</p>
                    </div>
                  )}
                  
                  {card.sampleCode && (
                    <div className="card-sample-code">
                      <h6>💻 Sample Code:</h6>
                      <div className="code-display-card">
                        <div className="code-header-card">
                          <span>Python</span>
                          <button
                            className="copy-code-btn-card"
                            onClick={() => {
                              navigator.clipboard.writeText(card.sampleCode);
                              alert('Code copied to clipboard!');
                            }}
                          >
                            📋 Copy
                          </button>
                        </div>
                        <pre className="code-block-card">
                          <code>{card.sampleCode}</code>
                        </pre>
                      </div>
                    </div>
                  )}
                  
                  {card.explanation && (
                    <div className="card-explanation">
                      <h6>💡 Explanation:</h6>
                      <p>{card.explanation}</p>
                    </div>
                  )}
                  
                  {card.examples && card.examples.length > 0 && (
                    <div className="card-examples">
                      <h6>📝 Examples:</h6>
                      <ul>
                        {card.examples.map((example, idx) => (
                          <li key={idx}>{example}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="card-footer">
                    {!isCompleted ? (
                      <button
                        className="mark-complete-btn"
                        onClick={() => handleCardComplete(card.id || index)}
                      >
                        ✅ Mark as Completed
                      </button>
                    ) : (
                      <div className="completed-message">
                        <span>🎉 You've completed this card!</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {completedCards.size > 0 && (
        <div className="cards-progress">
          <p>Progress: {completedCards.size} / {cards.length} cards completed</p>
          <div className="progress-bar-cards">
            <div
              className="progress-fill-cards"
              style={{ width: `${(completedCards.size / cards.length) * 100}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningCards;

